import { computed, Injectable, signal } from '@angular/core';
import { ActionEnhanced } from 'src/app/models/action/action.model';
import { CategoryEnhanced } from 'src/app/models/category/category.model';
import { ItemEnhanced } from 'src/app/models/item/item.model';
import { matchesQuery, toQueryWords, UniversalSearchResult } from './universal-search.models';

export interface ActiveInspectionSnapshot {
  workspaceId: string;
  folderId: string;
  inspectionId: string;
  title: string;
  categories: CategoryEnhanced[];
  items: ItemEnhanced[];
  actions: ActionEnhanced[];
}

const MAX_RESULTS_PER_KIND = 8;

/**
 * Holds the contents of the inspection the user currently has open so that Universal Search can
 * offer categories, items and actions without issuing any further reads. Screens that already
 * listen to this data replace the snapshot as a whole; nothing here starts a listener of its own.
 */
@Injectable({
  providedIn: 'root',
})
export class ActiveInspectionContextService {
  private readonly snapshot = signal<ActiveInspectionSnapshot | null>(null);

  public readonly hasContext = computed(() => this.snapshot() !== null);

  public titleForWorkspace(workspaceId: string | null): string | null {
    const context = this.snapshot();
    return context && context.workspaceId === workspaceId ? context.title : null;
  }

  public currentSnapshot(): ActiveInspectionSnapshot | null {
    return this.snapshot();
  }

  public setSnapshot(snapshot: ActiveInspectionSnapshot): void {
    this.snapshot.set(snapshot);
  }

  public clear(): void {
    this.snapshot.set(null);
  }

  /**
   * Drops the snapshot when the user has moved on to a different inspection route.
   */
  public clearIfNot(workspaceId: string, folderId: string, inspectionId: string | null): void {
    const current = this.snapshot();
    if (!current) return;
    if (current.workspaceId !== workspaceId || current.folderId !== folderId || current.inspectionId !== inspectionId) {
      this.snapshot.set(null);
    }
  }

  /**
   * @param workspaceId The workspace the user is currently in. A snapshot from another workspace is
   * never searched, so a stale context can not leak results across workspaces.
   */
  public search(query: string, workspaceId: string | null): UniversalSearchResult[] {
    const context = this.snapshot();
    const queryWords = toQueryWords(query);
    if (!context || !workspaceId || context.workspaceId !== workspaceId || queryWords.length === 0) return [];

    const inspectionPath = ['/workspace', context.workspaceId, 'folders', context.folderId, 'inspections', context.inspectionId];

    const categories = context.categories
      .filter((category) => matchesQuery([category.title, category.notes], queryWords))
      .slice(0, MAX_RESULTS_PER_KIND)
      .map(
        (category): UniversalSearchResult => ({
          kind: 'category',
          id: category.id,
          title: category.title,
          subtitle: context.title,
          commands: [...inspectionPath, 'categories', category.id, 'items'],
        })
      );

    const items = context.items
      .filter((item) => matchesQuery([item.title, item.notes], queryWords))
      .slice(0, MAX_RESULTS_PER_KIND)
      .map(
        (item): UniversalSearchResult => ({
          kind: 'item',
          id: item.id,
          title: item.title,
          subtitle: this.categoryTitle(context, item.category_id),
          commands: [...inspectionPath, 'categories', item.category_id, 'items', item.id],
        })
      );

    const actions = context.actions
      .filter((action) => matchesQuery([action.title, action.assignee_name, action.item_title], queryWords))
      .slice(0, MAX_RESULTS_PER_KIND)
      .map(
        (action): UniversalSearchResult => ({
          kind: 'action',
          id: action.id,
          title: action.title,
          subtitle: [action.item_title, action.assignee_name].filter(Boolean).join(' · '),
          commands: [...inspectionPath, 'categories', action.category_id, 'items', action.item_id],
        })
      );

    return [...categories, ...items, ...actions];
  }

  private categoryTitle(context: ActiveInspectionSnapshot, categoryId: string): string {
    return context.categories.find((category) => category.id === categoryId)?.title ?? context.title;
  }
}
